import { array } from 'fp-ts';
import { Card, CardContent, Stack } from '@optimus-web/core';
import { LocalizedSmallLabel, LocalizedSmallParagraph } from '@mews-commander/ui';
import { TextKey } from '@mews-commander/enums';
import { WithServiceId } from '@mews-commander/types';
import { isCurrentEnterpriseInMultiTaxEnvironment } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { getReservationsNonDefaultProductOrderItems } from '@mews-commander/legacy-entities/src/selectors/orderItems';
import { getCurrentEnterpriseReservationRateId } from '@mews-commander/legacy-entities/src/selectors/reservations';
import { useCommanderSelector } from '@mews-commander/utils';
import {
    getSortedActiveDefaultProductOrderItems,
    getSortedNonCanceledReservationsAdditionalExpenses,
} from '../../../selectors/orderItems';
import ReservationsTotalAmount from '../../reservationListItem/ReservationsTotalAmount';
import { getSortedProductOrderGroups } from '../../../transformProductOrders/groupProductOrders';
import AdditionalExpensesGrid from './AdditionalExpensesGrid';
import DefaultProductOrderItemsGrid from './DefaultProductOrderItemsGrid';
import ProductOrdersGrid from './ProductOrdersGrid';

interface ReservationOrderItemsProps extends WithServiceId {
    reservationId: string;
}

const ReservationOrderItems = ({ reservationId, serviceId }: ReservationOrderItemsProps) => {
    const rateId = useCommanderSelector((state) => getCurrentEnterpriseReservationRateId(state, { id: reservationId }));
    const isMultiTaxEnvironment: boolean = useCommanderSelector(isCurrentEnterpriseInMultiTaxEnvironment);
    const productOrders = useCommanderSelector((state) =>
        getReservationsNonDefaultProductOrderItems(state, { serviceOrderIds: [reservationId], serviceId })
    );
    const additionalExpenses = useCommanderSelector((state) =>
        getSortedNonCanceledReservationsAdditionalExpenses(state, { serviceOrderIds: [reservationId] })
    );
    const defaultProductOrderItems = useCommanderSelector((state) =>
        getSortedActiveDefaultProductOrderItems(state, { serviceOrderIds: [reservationId], serviceId })
    );
    const productOrderGroups = useCommanderSelector((state) =>
        getSortedProductOrderGroups(state, {
            serviceOrderIds: [reservationId],
            serviceId,
            withCanceledProductOrders: false,
        })
    );

    const areProductOrdersNonEmpty = array.isNonEmpty(productOrders);
    const areAdditionalExpensesNonEmpty = array.isNonEmpty(additionalExpenses);
    const areDefaultProductOrderItemsNonEmpty = array.isNonEmpty(defaultProductOrderItems);

    return (
        <Card overflowHidden padding="0" data-test-smart-detail-card="order-items">
            {areProductOrdersNonEmpty || areAdditionalExpensesNonEmpty || areDefaultProductOrderItemsNonEmpty ? (
                <CardContent padding="200">
                    <Stack vertical spacing="200">
                        {areDefaultProductOrderItemsNonEmpty ? (
                            <>
                                <LocalizedSmallLabel textKey={TextKey.Rate} as="div" />
                                {rateId && (
                                    <DefaultProductOrderItemsGrid
                                        rateId={rateId}
                                        serviceId={serviceId}
                                        defaultProductOrderItems={defaultProductOrderItems}
                                    />
                                )}
                            </>
                        ) : null}
                        {areProductOrdersNonEmpty || areAdditionalExpensesNonEmpty ? (
                            <>
                                <LocalizedSmallLabel textKey={TextKey.Items} as="div" />
                                <ProductOrdersGrid
                                    serviceId={serviceId}
                                    groups={productOrderGroups}
                                    productOrders={productOrders}
                                />

                                <AdditionalExpensesGrid serviceId={serviceId} additionalExpenses={additionalExpenses} />
                            </>
                        ) : null}
                    </Stack>
                </CardContent>
            ) : null}
            <CardContent padding="200" highlighted data-test-smart-detail-card-subpart="total-amount">
                <Stack distribution="space-between">
                    <LocalizedSmallParagraph textKey={TextKey.TotalAmount} />
                    <ReservationsTotalAmount serviceOrderIds={[reservationId]} net={isMultiTaxEnvironment} />
                </Stack>
            </CardContent>
        </Card>
    );
};

export default ReservationOrderItems;
